import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ExternalLink, CreditCard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspacePayoutStatus } from '@/hooks/useWorkspacePayoutStatus';

const BillingSettings = () => {
  const { userSettings } = useAuth();
  const workspaceId = userSettings?.activeWorkspaceId;
  const payoutStatus = useWorkspacePayoutStatus(workspaceId);
  const planName = userSettings?.activeWorkspace?.planDetails?.name || 'Seed';
  const subStatus = userSettings?.activeWorkspace?.stripeSubscriptionStatus;
  const statusLabel = subStatus ? subStatus.replace('_', ' ') : 'not subscribed';

  return (
    <div className="space-y-8">
       <Card className="rounded-3xl border-slate-200 bg-white">
        <CardHeader className="p-8 pb-4">
          <CardTitle className="text-2xl tracking-[-0.03em] text-slate-950">Membership</CardTitle>
          <CardDescription className="text-slate-600">Manage your subscription and payment methods.</CardDescription>
        </CardHeader>
        <CardContent className="p-8 pt-2 space-y-6">
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
             <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80">
                    <CreditCard size={24} />
                </div>
                <div>
                    <div className="font-semibold tracking-[-0.02em] text-slate-900">{planName} Plan</div>
                    <div className="text-sm capitalize text-slate-500">Status: {statusLabel}</div>
                </div>
             </div>
             <Button asChild variant="outline" className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                <Link to="/subscriptions">
                  View Plans
                </Link>
             </Button>
          </div>

          <Button asChild className="h-12 w-full rounded-xl bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/subscriptions">
              Manage Billing <ExternalLink className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600">
            <p className="font-semibold text-slate-900">Billing clarity</p>
            <p className="mt-1">
              LendLifted plan charges are managed here. Transaction processing and financing-provider fees (Stripe, Klarna, Afterpay/Clearpay, Affirm when available) apply separately at checkout.
            </p>
          </div>

          {!payoutStatus.isLoading && payoutStatus.pendingPayoutBalanceCents > 0 && (
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">
                Pending cashout: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                  payoutStatus.pendingPayoutBalanceCents / 100
                )}
              </p>
              <p className="mt-1">
                You have {payoutStatus.pendingPayoutCount} held {payoutStatus.pendingPayoutCount === 1 ? 'payment' : 'payments'} waiting for payout setup.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild className="h-10 rounded-xl bg-slate-900 text-white hover:bg-slate-800">
                  <Link to="/onboarding">Finish payout setup</Link>
                </Button>
                <Button asChild variant="outline" className="h-10 rounded-xl border-slate-200 bg-white hover:bg-slate-50">
                  <Link to="/settings/payouts">View payout ledger</Link>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSettings;
